// dataProcessing.tsx
// this file handles data filtering and sorting for the SQL table component

export const applyMonthFilter = (
    data: Record<string, any>[],
    config: {
        monthFilter: string;
        monthNavigation: boolean;
    }
): Record<string, any>[] => {
    
    if (!config.monthNavigation || !config.monthFilter) {
        return data;
    }
    
    const [monthStr, yearStr] = config.monthFilter.split(' ');
    
    if (!yearStr || !monthStr) {
        return data;
    }
    
    return data.filter(row => {
        const rowMonth = String(row.month);
        const rowYear = String(row.year);
        return rowMonth === monthStr && rowYear === yearStr;
    });
};

export const applyWarningFilters = (
    data: Record<string, any>[],
    config: {
        warningFilterFunction?: (row: any) => boolean;
        warningGroupKeyFunction?: (row: any) => string | null;
    }
): Record<string, any>[] => {

    if (!config.warningFilterFunction) {
        return data;
    }

    const filtered = data.filter(config.warningFilterFunction);

    if (!config.warningGroupKeyFunction) {
        return filtered;
    }

    // Sort rows that share a group key (e.g. potential duplicates) next to each
    // other, breaking ties by original position so unrelated rows keep their order.
    const groupKeyFn = config.warningGroupKeyFunction;
    return filtered
        .map((row, index) => ({ row, index, key: groupKeyFn(row) }))
        .sort((a, b) => {
            if (a.key == null && b.key == null) return a.index - b.index;
            if (a.key == null) return 1;
            if (b.key == null) return -1;
            if (a.key === b.key) return a.index - b.index;
            return a.key < b.key ? -1 : 1;
        })
        .map(item => item.row);
};

// Apply column filters (from TableHeader)
export const applyColumnFilters = (
    data: Record<string, any>[],
    config: {
        activeFilters: Record<string, Set<string>>;
        columnConfigs: any[];
        userData?: any;
    }
): Record<string, any>[] => {
        
    if (!config.activeFilters || Object.keys(config.activeFilters).length === 0) {
        return data;
    }
    
    return data.filter(row => {
        for (const [columnName, allowedValues] of Object.entries(config.activeFilters)) {
            let cellValue = row[columnName];
            
            const columnConfig = config.columnConfigs.find((c: any) => c.colName === columnName);
            
            // Apply conditionalDisplay if defined
            if (columnConfig?.conditionalDisplay && config.userData) {
                cellValue = columnConfig.conditionalDisplay(cellValue, row, config.userData);
            }
            
            let stringValue: string;
            const isJsonColumn = columnConfig?.colType === 'json';
            
            if (cellValue === null || cellValue === undefined || cellValue === '') {
                stringValue = '(Empty)';
            } else if (isJsonColumn) {
                stringValue = getJsonDisplayValue(cellValue);
            } else {
                stringValue = String(cellValue);
            }
            
            if (!allowedValues.has(stringValue)) {
                return false;
            }
        }
        return true;
    });
};

const getJsonDisplayValue = (value: any): string => {
    try {
        const jsonData = typeof value === 'string' ? JSON.parse(value) : value;
        if (Array.isArray(jsonData)) {
            return `${jsonData.length} items`;
        } else if (typeof jsonData === 'object' && jsonData !== null) {
            const keys = Object.keys(jsonData);
            return `${keys.length} items`;
        } else {
            return String(value);
        }
    } catch (error) {
        return String(value);
    }
};

const multiColumnSort = (
    data: Record<string, any>[],
    columns: string[],
    sortDirections: Record<string, 'asc' | 'desc'>
): Record<string, any>[] => {
    if (!columns || columns.length === 0) return data;
    
    return [...data].sort((a, b) => {
        for (let i = 0; i < columns.length; i++) {
            const col = columns[i];
            const direction = sortDirections[col] === 'desc' ? -1 : 1;
            
            let aVal = a[col];
            let bVal = b[col];
            
            // Handle null/undefined values (put them at the end)
            if (aVal == null && bVal == null) continue;
            if (aVal == null) return 1;
            if (bVal == null) return -1;
            
            // Convert to comparable types
            if (typeof aVal === 'string' && typeof bVal === 'string') {
                // Try to convert to numbers if they look numeric
                const aNum = Number(aVal);
                const bNum = Number(bVal);
                if (!isNaN(aNum) && !isNaN(bNum)) {
                    aVal = aNum;
                    bVal = bNum;
                } else {
                    // Case-insensitive string comparison
                    aVal = aVal.toLowerCase();
                    bVal = bVal.toLowerCase();
                }
            }
            
            if (aVal < bVal) return -1 * direction;
            if (aVal > bVal) return 1 * direction;
        }
        return 0;
    });
};

export const applySorting = (
    data: Record<string, any>[],
    config: {
        activeSorts?: Record<string, 'asc' | 'desc' | 'none'>; // User-managed sorts from header
        sortBy?: string[];
        sortOrder?: Record<string, 'asc' | 'desc'>;
    }
): Record<string, any>[] => {
    
    // Priority 1: User-managed sorts (from clicking headers)
    if (config.activeSorts && Object.keys(config.activeSorts).length > 0) {
        const columnsToSort = Object.keys(config.activeSorts).filter(
            col => config.activeSorts![col] !== 'none'
        );
        if (columnsToSort.length > 0) {
            const sortDirections = columnsToSort.reduce((acc, col) => {
                acc[col] = config.activeSorts![col] as 'asc' | 'desc';
                return acc;
            }, {} as Record<string, 'asc' | 'desc'>);
            return multiColumnSort(data, columnsToSort, sortDirections);
        }
    }
    
    // Priority 2: sortOrder prop (initial defaults)
    if (config.sortOrder && Object.keys(config.sortOrder).length > 0) {
        const columnsToSort = Object.keys(config.sortOrder);
        return multiColumnSort(data, columnsToSort, config.sortOrder);
    } 
    
    // Priority 3: sortBy prop (fallback to 'asc' for all)
    if (config.sortBy && config.sortBy.length > 0) {
        const defaultSortOrder = config.sortBy.reduce((acc, col) => {
            acc[col] = 'asc';
            return acc;
        }, {} as Record<string, 'asc' | 'desc'>);
        return multiColumnSort(data, config.sortBy, defaultSortOrder);
    }
    
    return data;
};


export type WarningTypes = 'warning' | 'alert' | 'pdf_alert' | 'suppressed';

// A warning rule's `key` must be a stable, unique-per-table slug (e.g.
// 'potential_duplicate_ticket') — it's what gets stored in a row's
// ignored_warnings once a warning is resolved, so unlike `description` it
// must never change once in use and must be unique even when two rules
// share the same `colName`.
export interface WarningRule {
    type?: 'warning' | 'alert' | 'pdf_alert';
    colName?: string;
    description?: string;
    key: string;
    condition: (rowData: any, allRows?: any[]) => boolean | { isMatch: boolean; data?: any };
    groupKey?: (rowData: any) => string | null;
}

export interface CellWarning {
    type: WarningTypes;
    data?: any;
    key: string;
    description: string;
}

const isWarningIgnored = (rowData: any, ruleKey: string | undefined): boolean => {
    if (!ruleKey || !rowData?.ignored_warnings) return false;
    try {
        const parsed = typeof rowData.ignored_warnings === 'string'
            ? JSON.parse(rowData.ignored_warnings)
            : rowData.ignored_warnings;
        return Array.isArray(parsed) && parsed.includes(ruleKey);
    } catch {
        return false;
    }
};

// warningRules arrays are typed as any[] in tableConfig.tsx, so a missing `key` isn't
// caught at compile time. Without a fallback, two key-less rules would collide: same
// Map entry for icon refs, same open/closed state for the resolve menu, and a resolve
// request would submit new_value: undefined (lands as SQL NULL). The derived fallback
// isn't as stable as a real key (it changes if colName/description ever change, which
// would orphan any already-resolved row for that rule), so this also logs an error to
// surface the gap — add an explicit `key` to the rule to fix it properly.
const getRuleKey = (rule: any): string => {
    if (rule.key) return rule.key;
    const fallback = `auto_${rule.colName || 'unknown'}_${(rule.description || 'rule').toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
    console.error(`Warning rule is missing a stable "key" — falling back to "${fallback}". Add an explicit key to this rule (colName: ${rule.colName}, description: ${rule.description}) so its resolve/ignore behavior doesn't break if colName or description ever changes.`);
    return fallback;
};

// Evaluate warning rules and group by rule type
export const evaluateWarningRules = (
    data: Record<string, any>[],
    config: {
        enableWarnings: boolean;
        warningRules: any[];
        idColumn: string;
        columnConfigs: any[];
    }
): {
    hasWarnings: boolean;
    warningCounter?: number;
    warningsByRule: Array<{
        rule: any;
        key: string;
        colName: string;
        affectedRowsCount: number;
        description: string;
        filterFunction: (row: any) => boolean;
        groupKeyFunction?: (row: any) => string | null;
    }>;

    hasAlerts: boolean;
    alertCounter?: number;
    alertsByRule: Array<{
        rule: any;
        key: string;
        colName: string;
        affectedRowsCount: number;
        description: string;
        filterFunction: (row: any) => boolean;
        groupKeyFunction?: (row: any) => string | null;
    }>;

    hasPdfAlerts: boolean;
    pdfAlertCounter?: number;
    pdfAlertsByRule: Array<{
        rule: any;
        key: string;
        colName: string;
        affectedRowsCount: number;
        description: string;
        filterFunction: (row: any) => boolean;
        groupKeyFunction?: (row: any) => string | null;
    }>;

    hasSuppressed: boolean;
    suppressedCounter?: number;
    suppressedByRule: Array<{
        rule: any;
        key: string;
        colName: string;
        affectedRowsCount: number;
        description: string;
        filterFunction: (row: any) => boolean;
        groupKeyFunction?: (row: any) => string | null;
    }>;

    warningsByRow: Map<number, Map<string, CellWarning[]>>;
    // Rows where a rule still matches today but is suppressed via ignored_warnings —
    // an admin-only signal for reconciliation, kept separate from warningsByRow since
    // it isn't tied to a specific visible column.
    ignoredActiveWarningsByRow: Map<number, CellWarning[]>;
} => {

    if (!config.enableWarnings || !config.warningRules || config.warningRules.length === 0 || !data || data.length === 0) {
        return {
            hasWarnings: false,
            warningsByRule: [],
            hasAlerts: false,
            alertsByRule: [],
            hasPdfAlerts: false,
            pdfAlertsByRule: [],
            hasSuppressed: false,
            suppressedByRule: [],
            warningsByRow: new Map<number, Map<string, CellWarning[]>>(),
            ignoredActiveWarningsByRow: new Map<number, CellWarning[]>()
        };
    }

    const warningsByRule: Array<{
        rule: any;
        key: string;
        colName: string;
        affectedRowsCount: number;
        description: string;
        filterFunction: (row: any) => boolean;
        groupKeyFunction?: (row: any) => string | null;
    }> = [];

    const alertsByRule: Array<{
        rule: any;
        key: string;
        colName: string;
        affectedRowsCount: number;
        description: string;
        filterFunction: (row: any) => boolean;
        groupKeyFunction?: (row: any) => string | null;
    }> = [];

    const pdfAlertsByRule: Array<{
        rule: any;
        key: string;
        colName: string;
        affectedRowsCount: number;
        description: string;
        filterFunction: (row: any) => boolean;
        groupKeyFunction?: (row: any) => string | null;
    }> = [];

    const suppressedByRule: Array<{
        rule: any;
        key: string;
        colName: string;
        affectedRowsCount: number;
        description: string;
        filterFunction: (row: any) => boolean;
        groupKeyFunction?: (row: any) => string | null;
    }> = [];

    const warningsByRow = new Map<number, Map<string, CellWarning[]>>();
    const ignoredActiveWarningsByRow = new Map<number, CellWarning[]>();

    config.warningRules.forEach(rule => {
        let affectedRowsCount = 0;
        let suppressedRowsCount = 0;
        const ruleKey = getRuleKey(rule);
        const ruleType: WarningTypes =
            rule.type === 'alert' ? 'alert' :
            rule.type === 'pdf_alert' ? 'pdf_alert' :
            'warning';

        // Count rows that match this rule
        data.forEach(rowData => {
            try {
                // Check if condition function exists and call it
                let conditionResult: boolean | { isMatch: boolean; data?: any } = false;

                if (rule.condition && typeof rule.condition === 'function') {
                    // Pass full visible dataset as an optional 2nd arg for cross-row rules.
                    conditionResult = rule.condition(rowData, data);
                }

                // Handle both boolean and object returns
                const isMatch = typeof conditionResult === 'boolean' ? conditionResult : conditionResult.isMatch;
                const metadata = typeof conditionResult === 'object' ? conditionResult.data : undefined;

                if (isMatch && !isWarningIgnored(rowData, ruleKey)) {
                    affectedRowsCount++;
                    const rowId = rowData[config.idColumn];

                    // Initialize the map for this row if it doesn't exist
                    if (!warningsByRow.has(rowId)) {
                        warningsByRow.set(rowId, new Map<string, CellWarning[]>());
                    }

                    const rowWarnings = warningsByRow.get(rowId)!;

                    const warningInfo: CellWarning = {
                        type: ruleType,
                        data: metadata,
                        key: ruleKey,
                        description: rule.description || ''
                    };

                    // A cell can carry more than one warning (multiple rules on the
                    // same column) — append rather than overwrite.
                    const appendWarning = (colName: string) => {
                        rowWarnings.set(colName, [...(rowWarnings.get(colName) ?? []), warningInfo]);
                    };

                    // If rule specifies a column, add just that; else mark all configured columns
                    if (rule.colName) {
                        appendWarning(rule.colName);
                    } else {
                        config.columnConfigs.forEach(c => appendWarning(c.colName));
                    }
                } else if (isMatch && isWarningIgnored(rowData, ruleKey)) {
                    // Still matches today but is suppressed — track separately for the
                    // admin-only reconciliation indicator/filter, not the normal cell display.
                    suppressedRowsCount++;
                    const rowId = rowData[config.idColumn];
                    const warningInfo: CellWarning = {
                        type: ruleType,
                        data: metadata,
                        key: ruleKey,
                        description: rule.description || ''
                    };
                    ignoredActiveWarningsByRow.set(rowId, [...(ignoredActiveWarningsByRow.get(rowId) ?? []), warningInfo]);
                }
            } catch (error) {
                console.error('Error evaluating warning rule:', error);
            }
        });

        if (affectedRowsCount > 0) {
            const ruleItem = {
                rule,
                key: ruleKey,
                colName: rule.colName || 'Unknown',
                affectedRowsCount,
                description: rule.description || `Issues in ${rule.colName}`,
                // Wrap rather than pass rule.condition directly: Array.prototype.filter
                // invokes callbacks as (element, index, array), which would clobber the
                // rule's `allRows` cross-row argument with a numeric index. Also coerce
                // {isMatch, data} results to a boolean — an object is always truthy, so
                // an {isMatch: false, ...} return would otherwise wrongly pass the filter.
                // And exclude resolved warnings, matching the isMatch check above — otherwise
                // a resolved row still passes this filter (condition still true on the raw
                // data) even though its warning icon is correctly suppressed.
                filterFunction: (row: any) => {
                    if (isWarningIgnored(row, ruleKey)) return false;
                    const result = rule.condition(row, data);
                    if (typeof result === 'boolean') return result;
                    return !!(result && result.isMatch);
                },
                groupKeyFunction: typeof rule.groupKey === 'function' ? rule.groupKey : undefined
            };
            
            // Separate by type - default to 'warning' if not specified
            if (rule.type === 'alert') {
                alertsByRule.push(ruleItem);
            } else if (rule.type === 'pdf_alert') {
                pdfAlertsByRule.push(ruleItem);
            } else {
                warningsByRule.push(ruleItem);
            }
        }

        if (suppressedRowsCount > 0) {
            suppressedByRule.push({
                rule,
                key: ruleKey,
                colName: rule.colName || 'Unknown',
                affectedRowsCount: suppressedRowsCount,
                description: rule.description || `Issues in ${rule.colName}`,
                // Inverse of the non-ignored filterFunction above: only rows where this
                // rule is BOTH ignored AND still matches today (an active suppression).
                filterFunction: (row: any) => {
                    if (!isWarningIgnored(row, ruleKey)) return false;
                    const result = rule.condition(row, data);
                    if (typeof result === 'boolean') return result;
                    return !!(result && result.isMatch);
                },
                groupKeyFunction: typeof rule.groupKey === 'function' ? rule.groupKey : undefined
            });
        }
    });

    return {
        hasWarnings: warningsByRule.length > 0,
        warningCounter: warningsByRule.reduce((sum, item) => sum + item.affectedRowsCount, 0),
        warningsByRule,
        hasAlerts: alertsByRule.length > 0,
        alertCounter: alertsByRule.reduce((sum, item) => sum + item.affectedRowsCount, 0),
        alertsByRule,
        hasPdfAlerts: pdfAlertsByRule.length > 0,
        pdfAlertCounter: pdfAlertsByRule.reduce((sum, item) => sum + item.affectedRowsCount, 0),
        pdfAlertsByRule,
        hasSuppressed: suppressedByRule.length > 0,
        suppressedCounter: suppressedByRule.reduce((sum, item) => sum + item.affectedRowsCount, 0),
        suppressedByRule,
        warningsByRow,
        ignoredActiveWarningsByRow,
    };
};